import React, { useEffect, useState, useContext } from 'react';
import PropTypes from 'prop-types';
import { UserContext } from './UserContext';
import { ConfigContext } from '../Config/ConfigContext';
import { AppBar, Toolbar, Typography } from '@mui/material';
import Auth from '../Auth';
import useApi from '../hooks/useApi';

/**
 * Provides user context to its children by fetching the user profile.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {*} props.LoginHeaderLogo - The logo displayed in the login header.
 * @param {React.ReactNode} props.children - The child components that will have access to the user context.
 * @returns {React.ReactElement} The UserProvider component.
 */
export const UserProvider = ({ AuthComponent, LoginHeaderLogo, signUpOptions, children }) => {
	const [user, setUser] = useState(null);
	const [loading, setLoading] = useState(true);
	const [fetchError, setFetchError] = useState(false);
	const { config } = useContext(ConfigContext);
	const api = useApi();

	/**
	 * Asynchronously fetches the user profile from the API and updates state accordingly.
	 *
	 * @async
	 * @function fetchUser
	 */
	const fetchUser = async () => {
		try {
			const data = await api.auth.profile();
			setUser(data);
			setFetchError(false);
		} catch (error) {
			console.error('Error fetching user:', error);
			setFetchError(true);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchUser();
	}, []);

    if (fetchError && AuthComponent) {
        return (
            <AuthComponent />
        );
    }

	if (fetchError) {
		return (
			<Auth
				LoginHeaderLogo={LoginHeaderLogo}
				signInBlurb="Sign in to your account"
                signUpOptions={signUpOptions}
			/>
		);
	}

	if (loading) {
		return (
			<AppBar position="static" style={{ height: '25px', backgroundColor: 'green' }}>
				<Toolbar variant="dense" style={{ minHeight: '25px' }}>
					<Typography variant="caption" color="white">
						Loading user data...
					</Typography>
				</Toolbar>
			</AppBar>
		);
	}

	return (
		<UserContext.Provider value={{ user, setUser, reloadUser: fetchUser }}>
			{children}
		</UserContext.Provider>
	);
};

UserProvider.propTypes = {
	LoginHeaderLogo: PropTypes.any,
	children: PropTypes.node.isRequired,
};
